import axios from "axios";
import { MerchantInterface, ResponseInterface } from "../interfaces";
import { Nullable } from "../interfaces/GenericTypes";
import { merchantFormat } from "../utils";
import { alertService } from "./alertService";

const testURL = "http://localhost:8085/vpos/ping";
const baseURL = "http://localhost:8085/vpos/metodo";
const printerURL = process.env.REACT_APP_API_PRINTER_HOST;

// Merchant misc options
enum MiscelaneousMerchantMethods {
  "imprimeUltimoVoucher",
  "imprimeUltimoVoucherP",
  "precierre",
  "cierre",
  "ultimoCierre",
}

// Vouchers available through the local printer service (GET /Voucher/{command})
export type MerchantVoucherCommand =
  | "aprobado"
  | "procesado"
  | "precierre"
  | "cierre";

// Request header
const header = {
  headers: {
    "Content-Type": "application/json",
  },
};

// Ping merchant service
export const pingMerchant = async (): Promise<Nullable<MerchantInterface>> => {
  try {
    const response = await axios.get(testURL);

    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {}

  return null;
};

// Pay through merchant service
export const payMerchant = async (
  amount: number,
  ci: string,
  zelleDescription?: string
): Promise<Nullable<MerchantInterface>> => {
  const amountString = merchantFormat(amount);

  const body = {
    accion: "tarjeta",
    montoTransaccion: amountString,
    cedula: ci,
    referencia: zelleDescription,
  };
  try {
    const response = await axios.post(baseURL, body, header);

    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {}

  return null;
};

export const annulationMerchant = async (
  seq: string
): Promise<Nullable<MerchantInterface>> => {
  const body = {
    accion: "anulacion",
    numSeq: seq,
  };
  try {
    const response = await axios.post(baseURL, body, header);

    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {}

  return null;
};

// Pay change with merchant service
export const changeMerchant = async (
  amount: number,
  ci: string
): Promise<Nullable<MerchantInterface>> => {
  const amountString = merchantFormat(amount);
  const body = {
    accion: "cambio",
    montoTransaccion: amountString,
    cedula: ci,
    tipoMoneda: "VES",
  };
  try {
    const response = await axios.post(baseURL, body, header);

    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {}

  return null;
};

// Use other merchant services
export const miscMerchant = async (
  command: number
): Promise<Nullable<MerchantInterface>> => {
  const body = {
    accion: MiscelaneousMerchantMethods[command],
  };
  try {
    const response = await axios.post(baseURL, body, header);

    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {}

  return null;
};

// Get the text of a merchant voucher from the local printer service
export const getMerchantVoucher = async (
  command: MerchantVoucherCommand
): Promise<ResponseInterface<string>> => {
  try {
    const response = await axios.get<ResponseInterface<string>>(
      `${printerURL}/Voucher/${command}`,
      header
    );

    if (response.status === 200) {
      return response.data;
    }

    return {
      message: "",
      didError: true,
      errorMessage: `No se pudo obtener el voucher '${command}'.`,
      model: null,
    };
  } catch (error) {
    // The service answers with BadRequest and the same shape when it fails
    const data = (error as any)?.response?.data as
      | ResponseInterface<string>
      | undefined;

    return {
      message: data?.message ?? "",
      didError: true,
      errorMessage:
        data?.errorMessage ??
        "No se pudo comunicar con el servicio de impresión, verifique que el programa se esté ejecutando.",
      model: null,
    };
  }
};

// Get the voucher text and notify the user when it could not be read
export const getMerchantVoucherText = async (
  command: MerchantVoucherCommand
): Promise<Nullable<string>> => {
  const response = await getMerchantVoucher(command);

  if (response.didError || !response.model) {
    alertService.error("No se pudo obtener el voucher", response.errorMessage);
    return null;
  }

  return response.model;
};

// Voucher of the last approved transaction (vpos.txt)
export const getApprovedVoucher = async (): Promise<Nullable<string>> =>
  await getMerchantVoucherText("aprobado");

// Voucher of the last processed transaction (ultTxnProc.txt)
export const getProcessedVoucher = async (): Promise<Nullable<string>> =>
  await getMerchantVoucherText("procesado");

// Latest pre-closing report (precierres folder)
export const getPreClosingVoucher = async (): Promise<Nullable<string>> =>
  await getMerchantVoucherText("precierre");

// Latest closing report (cierres folder)
export const getClosingVoucher = async (): Promise<Nullable<string>> =>
  await getMerchantVoucherText("cierre");
